'use client';
import { Swiper, SwiperSlide } from 'swiper/react';
import { Autoplay, Pagination } from 'swiper/modules';
import 'swiper/css';
import 'swiper/css/pagination';
import ShimmerImage from '@/components/ui/ShimmerImage';
import { useState } from 'react';
import { useGetFeaturedCompanyList } from '@/features/company';
import FeaturedCompaniesItem from './FeaturedCompaniesSlider';
import { FeaturedCompanyListDTO } from '@/shared';

function FeaturedCompaniesSkeleton() {
  return (
    <div className="grid grid-cols-3 gap-3 sm:grid-cols-4 md:grid-cols-6 lg:grid-cols-10">
      {Array.from({ length: 10 }).map((_, index) => (
        <div
          key={index}
          className="flex min-h-36 animate-pulse flex-col items-center justify-between gap-3 rounded-lg border border-gray-100 bg-white p-3"
        >
          <div className="h-16 w-16 rounded-full bg-gray-200" />
          <div className="h-10 w-full rounded bg-gray-200" />
          <div className="h-6 w-16 rounded bg-gray-200" />
        </div>
      ))}
    </div>
  );
}

const FeaturedCompanies = () => {
  const { data: companies, isLoading } = useGetFeaturedCompanyList();
  const [showModal, setShowModal] = useState(false);
  const [hoveredIndex, setHoveredIndex] = useState<number>(0);
  const canLoop = companies && companies.length > 1;

  if (!isLoading && (!companies || companies.length === 0)) return null;

  return (
    <div className="w-full pt-4 xl:pt-8">
      <div className="container mx-auto">
        <h3 className="text-sm ml-2 sm:ml-0 lg:text-lg font-medium pb-3 xl:pb-8">
          Онцлох байгууллагууд
        </h3>
        {/* <div className="w-full mx-auto flex flex-col gap-y-10 [ md:gap-y-16]">
          <h3 className="text-sm ml-2 sm:ml-0 lg:text-xl font-medium pb-3 lg:pb-8">
            Онцлох байгууллагууд
          </h3>
        </div> */}
        <div className="flex w-full items-center justify-center px-4 sm:px-0">
          <div className="relative w-full">
            {isLoading ? (
              <FeaturedCompaniesSkeleton />
            ) : (
              <Swiper
                modules={[Autoplay, Pagination]}
                pagination={{ clickable: true, dynamicBullets: true }}
                spaceBetween={12}
                slidesPerView={4}
                slidesPerGroup={1}
                autoplay={
                  canLoop ? { delay: 5000, pauseOnMouseEnter: true } : false
                }
                loop={canLoop}
                breakpoints={{
                  320: {
                    slidesPerView: 3,
                    slidesPerGroup: 3,
                    spaceBetween: 10,
                  },
                  480: {
                    slidesPerView: 4,
                    slidesPerGroup: 4,
                    spaceBetween: 10,
                  },
                  640: {
                    slidesPerView: 5,
                    slidesPerGroup: 1,
                    spaceBetween: 12,
                  },
                  768: {
                    slidesPerView: 6,
                    slidesPerGroup: 1,
                    spaceBetween: 12,
                  },
                  1024: {
                    slidesPerView: 8,
                    slidesPerGroup: 1,
                    spaceBetween: 12,
                  },
                  1280: {
                    slidesPerView: 10,
                    slidesPerGroup: 1,
                    spaceBetween: 12,
                  },
                }}
                className="w-full relative pb-8!"
              >
                {companies?.map((company: FeaturedCompanyListDTO) => {
                  return (
                    <SwiperSlide key={company.id}>
                      <div
                        onClick={() => {
                          setHoveredIndex(company.id);
                          setShowModal(true);
                        }}
                        className="rounded-lg min-h-[138px] bg-white w-full border border-[#f1f4f7] transition-all duration-100 ease-in-out p-3 hover:border-primary flex flex-col items-center justify-between cursor-pointer gap-[10px]"
                      >
                        <div className="w-[70px] h-[70px] flex justify-center items-center bg-white overflow-hidden rounded-full border-2 border-primary-100">
                          <ShimmerImage
                            src={`${company.logo}`}
                            alt={company.name}
                            objectID={company.id}
                            objectType="company"
                            width={100}
                            height={50}
                          />
                        </div>
                        <div className="text-[12px] text-center h-7 overflow-hidden tracking-tighter leading-[14px] line-clamp-2 text-[#333]">
                          {company.name}
                        </div>
                        <div className="text-[10px] text-[#003C77] mt-2">
                          {company.jobCount ?? 0} ажлын байр
                        </div>
                      </div>
                    </SwiperSlide>
                  );
                })}
              </Swiper>
            )}
          </div>
        </div>
        {!isLoading && (
          <FeaturedCompaniesItem
            companies={companies ?? []}
            isOpen={showModal}
            onClose={() => setShowModal(false)}
            initialCompanyId={hoveredIndex}
          />
        )}
      </div>
    </div>
  );
};

export default FeaturedCompanies;
